// reassign/pages/ReassignAdmin.jsx
// Reassignments → Admin Config (route /reassign/admin). Replaces the old non-functional
// "Targets & Caps" stub. Design: scratch/REASSIGNMENTS-V2-DESIGN.md (PR 1).
//
//   Access        who may use Reassignments and as what (rprs.ReassignAccess)
//   Admins by rule  super-users + ADMIN* staff: admins implicitly, listed read-only
//   v2 stage      preview (testers only) → live (everyone with a role). Super-users only.
//   Activity data rprs.SeatActivity freshness + "Refresh now"
//   History       every grant / change / revoke
//
// Every action is enforced server-side (api/reassign); this page only decides what to render.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  fetchAccessList, upsertAccess, removeAccess, setV2Stage,
  fetchReassignStaff, fetchActivityStatus, requestActivityRefresh,
} from '../services/reassignApi';
import '../ReassignV2.css';

const ROLE_HELP = {
  viewer: 'Read only',
  submitter: 'Build drafts and submit them for approval',
  approver: 'Approve submissions and confirm changes to PE',
  admin: 'Approver + this page',
};

function fmtTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

function fmtDuration(ms) {
  if (ms == null) return '—';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

export default function ReassignAdmin() {
  const { reassignAccess } = useOutletContext() || {};
  const isAdmin = reassignAccess?.role === 'admin';
  const isSuper = !!reassignAccess?.isSuperUser;

  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [activity, setActivity] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [list, act] = await Promise.all([fetchAccessList(), fetchActivityStatus()]);
      setData(list);
      setActivity(act);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    load();
    fetchReassignStaff().then(r => setStaff(r.staff || [])).catch(() => {});
  }, [isAdmin, load]);

  if (!reassignAccess) {
    return <div className="rv2-page"><div className="rv2-muted">Loading…</div></div>;
  }
  if (!isAdmin) {
    return (
      <div className="rv2-page">
        <h1 className="rv2-title">Reassignments — Admin Config</h1>
        <div className="rv2-card rv2-muted">You don't have access to this page.</div>
      </div>
    );
  }

  // Resolves true on success so callers can close their form only when the change landed.
  const run = async (fn, okMsg) => {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      await fn();
      if (okMsg) setNotice(okMsg);
      await load();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rv2-page">
      <h1 className="rv2-title">Reassignments — Admin Config</h1>
      {error && <div className="rv2-banner rv2-banner--error">{error}</div>}
      {notice && <div className="rv2-banner rv2-banner--ok">{notice}</div>}
      {!data ? (
        <div className="rv2-muted">Loading…</div>
      ) : (
        <>
          <AccessCard data={data} staff={staff} busy={busy} run={run} />
          <ImplicitCard implicit={data.implicit} />
          <StageCard config={data.config} isSuper={isSuper} busy={busy} run={run} />
          <ActivityCard activity={activity} setActivity={setActivity} />
          <HistoryCard log={data.log} />
        </>
      )}
    </div>
  );
}

// ── Access ──────────────────────────────────────────────────────────────────

function AccessCard({ data, staff, busy, run }) {
  const [editing, setEditing] = useState(null);   // { staffId, role, isPreviewTester, notes, name }
  const [query, setQuery] = useState('');

  const granted = useMemo(() => new Set(data.rows.map(r => r.StaffId)), [data.rows]);
  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return staff
      .filter(s => s.IsActive && s.StaffId && !granted.has(s.StaffId))
      .filter(s => s.Name.toLowerCase().includes(q) || (s.Email || '').includes(q))
      .slice(0, 8);
  }, [staff, query, granted]);

  const startAdd = (s) => {
    setEditing({ staffId: s.StaffId, name: s.Name, role: 'viewer', isPreviewTester: false, notes: '', isNew: true });
    setQuery('');
  };
  const startEdit = (r) => setEditing({
    staffId: r.StaffId, name: r.Name, role: r.Role, isPreviewTester: !!r.IsPreviewTester, notes: r.Notes || '', isNew: false,
  });

  const save = () => run(
    () => upsertAccess(editing),
    `${editing.isNew ? 'Granted' : 'Updated'} ${editing.name} (${editing.role}${editing.isPreviewTester ? ', preview tester' : ''}).`,
  ).then(ok => { if (ok) setEditing(null); });

  const remove = (r) => {
    if (!window.confirm(`Remove ${r.Name}'s Reassignments access?`)) return;
    run(() => removeAccess(r.StaffId), `Removed ${r.Name}.`);
  };

  return (
    <section className="rv2-card">
      <div className="rv2-card-head">
        <h2>Access</h2>
        <div className="rv2-muted">
          {data.roles.map(r => <span key={r} className="rv2-role-hint"><b>{r}</b> — {ROLE_HELP[r]}</span>)}
        </div>
      </div>

      <div className="rv2-add">
        <input
          className="rv2-input"
          placeholder="Add someone — type a name or email"
          value={query}
          onChange={e => setQuery(e.target.value)}
          disabled={busy}
        />
        {candidates.length > 0 && (
          <ul className="rv2-suggest">
            {candidates.map(s => (
              <li key={s.StaffIndex}>
                <button type="button" onClick={() => startAdd(s)}>
                  {s.Name} <span className="rv2-muted">· {s.Level || '—'} · {s.Office} {s.Dept}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <div className="rv2-edit">
          <div className="rv2-edit-name">{editing.name}</div>
          <label>
            Role{' '}
            <select value={editing.role} onChange={e => setEditing({ ...editing, role: e.target.value })}>
              {data.roles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="rv2-check">
            <input
              type="checkbox"
              checked={editing.isPreviewTester}
              onChange={e => setEditing({ ...editing, isPreviewTester: e.target.checked })}
            />
            v2 preview tester
          </label>
          <input
            className="rv2-input rv2-input--notes"
            placeholder="Notes (optional)"
            value={editing.notes}
            maxLength={400}
            onChange={e => setEditing({ ...editing, notes: e.target.value })}
          />
          <button className="sched-admin-row-btn sched-admin-row-btn--primary" disabled={busy} onClick={save}>
            {editing.isNew ? 'Grant' : 'Save'}
          </button>
          <button className="sched-admin-row-btn" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
        </div>
      )}

      {data.rows.length === 0 ? (
        <div className="rv2-muted rv2-empty">
          No one has been granted access through this list yet. Super-users and ADMIN* staff are admins by rule (below).
        </div>
      ) : (
        <table className="rv2-table">
          <thead>
            <tr><th>Name</th><th>Role</th><th>Preview</th><th>Level</th><th>Office</th><th>Notes</th><th>Granted</th><th /></tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.StaffId} className={editing?.staffId === r.StaffId ? 'sched-admin-table-row--editing' : ''}>
                <td>
                  {r.Name}
                  {!r.IsActive && <span className="rv2-tag rv2-tag--warn">inactive</span>}
                  <div className="rv2-muted">{r.Email}</div>
                </td>
                <td><span className={`rv2-tag rv2-tag--${r.Role}`}>{r.Role}</span></td>
                <td>{r.IsPreviewTester ? 'Yes' : '—'}</td>
                <td>{r.Level || '—'}</td>
                <td>{r.Office || '—'}</td>
                <td className="rv2-notes">{r.Notes || ''}</td>
                <td className="rv2-muted">{r.GrantedBy}<br />{fmtTime(r.GrantedAt)}</td>
                <td className="rv2-actions">
                  <button className="sched-admin-row-btn" disabled={busy} onClick={() => startEdit(r)}>Edit</button>
                  <button className="sched-admin-row-btn sched-admin-row-btn--danger" disabled={busy} onClick={() => remove(r)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ── Admins by rule ──────────────────────────────────────────────────────────

function ImplicitCard({ implicit }) {
  return (
    <section className="rv2-card">
      <div className="rv2-card-head">
        <h2>Admins by rule</h2>
        <div className="rv2-muted">Always admins; not editable here. Add an ADMIN* person to the list above only to make them a v2 preview tester.</div>
      </div>
      <div className="rv2-implicit">
        <div>
          <h3>Super-users</h3>
          <ul>{(implicit?.superUsers || []).map(e => <li key={e}>{e}</li>)}</ul>
        </div>
        <div>
          <h3>ADMIN* department</h3>
          <ul>{(implicit?.adminDept || []).map(s => <li key={s.StaffId}>{s.Name} <span className="rv2-muted">· {s.Dept}</span></li>)}</ul>
        </div>
      </div>
    </section>
  );
}

// ── v2 stage ────────────────────────────────────────────────────────────────

function StageCard({ config, isSuper, busy, run }) {
  const stage = config?.['v2.stage']?.value || 'preview';
  const meta = config?.['v2.stage'];
  const flip = (value) => {
    const msg = value === 'live'
      ? 'Go LIVE? Everyone with a Reassignments role will see the v2 pages.'
      : 'Back to PREVIEW? Only super-users and preview testers will see the v2 pages.';
    if (!window.confirm(msg)) return;
    run(() => setV2Stage(value), `v2 stage set to ${value}.`);
  };
  return (
    <section className="rv2-card">
      <div className="rv2-card-head">
        <h2>v2 stage</h2>
        <div className="rv2-muted">
          <b>preview</b>: v2 pages for super-users and preview testers only. <b>live</b>: for everyone with a role (the cutover).
        </div>
      </div>
      <div className="rv2-stage">
        <span className={`rv2-tag rv2-tag--stage-${stage}`}>{stage}</span>
        {meta && <span className="rv2-muted">set by {meta.updatedBy} · {fmtTime(meta.updatedAt)}</span>}
        {isSuper ? (
          stage === 'preview'
            ? <button className="sched-admin-row-btn sched-admin-row-btn--primary" disabled={busy} onClick={() => flip('live')}>Go live</button>
            : <button className="sched-admin-row-btn" disabled={busy} onClick={() => flip('preview')}>Back to preview</button>
        ) : (
          <span className="rv2-muted">Only super-users can change this.</span>
        )}
      </div>
    </section>
  );
}

// ── Activity data ───────────────────────────────────────────────────────────

function ActivityCard({ activity, setActivity }) {
  const [polling, setPolling] = useState(false);
  const [err, setErr] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const refresh = async () => {
    if (!window.confirm(
      'Rebuild the 12-month activity data now?\n\n'
      + 'It is a full scan of the WIP table on the shared database (it runs nightly between 2 and 6am anyway). '
      + 'It is skipped automatically if the database is busy.')) return;
    setErr(null);
    try {
      await requestActivityRefresh({ force: false });
      setPolling(true);
      const started = Date.now();
      clearInterval(timer.current);
      timer.current = setInterval(async () => {
        try {
          const a = await fetchActivityStatus();
          setActivity(a);
          const latest = a.runs?.[0];
          const done = latest && latest.Status !== 'running' && new Date(latest.StartedAt).getTime() >= started - 60000;
          if (done || Date.now() - started > 6 * 60 * 1000) { clearInterval(timer.current); setPolling(false); }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) {
      setErr(e.message);
    }
  };

  const runs = activity?.runs || [];
  const lastOk = runs.find(r => r.Status === 'ok');
  return (
    <section className="rv2-card">
      <div className="rv2-card-head">
        <h2>Activity data</h2>
        <div className="rv2-muted">
          Per client service: hours, billing and last activity over the last 12 months (DJB's activity toggle).
          Rebuilt nightly (2-6am Central) from the PE mirror.
        </div>
      </div>
      {err && <div className="rv2-banner rv2-banner--error">{err}</div>}
      <div className="rv2-stage">
        <span>{(activity?.rows ?? 0).toLocaleString()} client services · {(activity?.activeRows ?? 0).toLocaleString()} active</span>
        <span className="rv2-muted">last good refresh: {lastOk ? fmtTime(lastOk.FinishedAt) : 'never'}</span>
        <button className="sched-admin-row-btn" disabled={polling} onClick={refresh}>
          {polling ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>
      {runs.length > 0 && (
        <table className="rv2-table rv2-table--compact">
          <thead><tr><th>Run</th><th>Status</th><th>Started</th><th>Duration</th><th>Rows</th><th>By</th><th>Note</th></tr></thead>
          <tbody>
            {runs.map(r => (
              <tr key={r.RunId}>
                <td>{r.RunId}</td>
                <td><span className={`rv2-tag rv2-tag--run-${r.Status}`}>{r.Status}</span></td>
                <td>{fmtTime(r.StartedAt)}</td>
                <td>{fmtDuration(r.DurationMs)}</td>
                <td>{r.RowsWritten?.toLocaleString() ?? '—'}</td>
                <td>{r.TriggeredBy}</td>
                <td className="rv2-notes">{r.Note || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ── History ─────────────────────────────────────────────────────────────────

function HistoryCard({ log }) {
  if (!log?.length) return null;
  const describe = (l) => {
    if (l.Action === 'grant') return `granted ${l.NewRole}${l.NewPreview ? ' + preview' : ''}`;
    if (l.Action === 'revoke') return `revoked (was ${l.OldRole})`;
    const parts = [];
    if (l.OldRole !== l.NewRole) parts.push(`${l.OldRole} → ${l.NewRole}`);
    if (!!l.OldPreview !== !!l.NewPreview) parts.push(l.NewPreview ? 'preview on' : 'preview off');
    return parts.length ? parts.join(', ') : 'notes updated';
  };
  return (
    <section className="rv2-card">
      <div className="rv2-card-head"><h2>History</h2><div className="rv2-muted">Last 50 access changes</div></div>
      <table className="rv2-table rv2-table--compact">
        <thead><tr><th>When</th><th>Person</th><th>Change</th><th>By</th></tr></thead>
        <tbody>
          {log.map(l => (
            <tr key={l.LogId}>
              <td>{fmtTime(l.ChangedAt)}</td>
              <td>{l.Name || `#${l.StaffId}`}</td>
              <td>{describe(l)}</td>
              <td>{l.ChangedBy}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
